import { Avatar, Box, TextField, Typography, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { forgotPassword } from "../Redux/user/slice";
import LockResetIcon from "@mui/icons-material/LockReset";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

export function ForgotPasswordModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const { isFetching } = useSelector((state) => state.user);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    // Clear previous messages
    setEmailError("");
    setSuccessMessage("");
    
    // Validation
    if (!email) {
      setEmailError(<FormattedMessage id="forgotpassword.error.email.required" />);
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError(<FormattedMessage id="forgotpassword.error.email.invalid" />);
      return;
    }

    // Dispatch forgot password action
    dispatch(forgotPassword({ email }));
    
    // Show success message
    setSuccessMessage(<FormattedMessage id="forgotpassword.success.message" />);
    
    // Clear email field after 2 seconds and close modal
    setTimeout(() => {
      setEmail("");
      setSuccessMessage("");
      onClose();
    }, 3000);
  };

  const handleClose = () => {
    setEmail("");
    setEmailError("");
    setSuccessMessage("");
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      style={{ height: "auto" }} 
      toggle={handleClose} 
      backdrop 
      onBackdropClick={handleClose}
    >    
      <ModalHeader style={{ justifyContent: "center", alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255,255,255,0.8)",
            padding: 2,
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockResetIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            <FormattedMessage id="forgotpassword.modal.title" />
          </Typography>
        </Box>
      </ModalHeader>
      <ModalBody>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexFlow: "column nowrap",
            width: "100%",
            rowGap: "1em",
            padding: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 2 }}>
            <FormattedMessage id="forgotpassword.modal.description" />
          </Typography>

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={<FormattedMessage id="forgotpassword.email.label" />}
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
            }}
            error={emailError !== ""}
            helperText={emailError}
            autoFocus
            sx={{ margin: "0" }}
            type="email"
            disabled={isFetching || successMessage !== ""}
          />

          {successMessage && (
            <Typography
              color="green"
              sx={{
                background: "#4caf5024",
                width: "100%",
                textAlign: "center",
                padding: 2,
                borderRadius: 1,
              }}
            >
              {successMessage}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2, width: "100%", mt: 2 }}>
            <Button 
              type="button" 
              onClick={handleClose}
              style={{ flex: 1 }}
              disabled={isFetching}
            >
              <FormattedMessage id="forgotpassword.button.cancel" />
            </Button>
            
            <Button 
              type="submit" 
              color="primary"
              style={{ flex: 1 }}
              disabled={isFetching || successMessage !== ""}
            >
              {isFetching ? (
                <CircularProgress size={24} />
              ) : (
                <FormattedMessage id="forgotpassword.button.send" />
              )}
            </Button>
          </Box>
        </Box>
      </ModalBody>
    </Modal>
  );
}
